/**
 * Measurements log, time-series visualization, and history.
 *
 * Layout (top to bottom):
 *   • Header + "+ Log measurement" button (opens MeasurementFormModal).
 *   • Category bar — All / Body / Vitals / Activity / Sleep / Other.
 *   • When a category is selected, a row of type chips for the metrics
 *     in that bucket. Clicking a type chip drills into a single-metric
 *     big chart with a W/M/3M/Y/All range filter.
 *   • Sparkline grid (when no type is focused) — one card per metric.
 *   • Recent log list at the bottom (filtered by type if focused).
 *
 * Manual entry sits behind a modal because in practice the data is
 * imported (RENPHO, Sleep Cycle, …) or synced by an agent — the page is
 * primarily about reading trends, not data entry.
 */

import { useMemo, useState } from 'react';
import {
  useMeasurements,
  type MeasurementDto,
} from '../../api/health';
import { MeasurementFormModal } from '../../components/MeasurementFormModal';
import { TimeSeriesChart, type ChartPoint } from '../../components/TimeSeriesChart';
import styles from './health.module.css';

// ─── Catalog ─────────────────────────────────────────────────────────────

/**
 * Canonical labels + units + category for every metric type the platform
 * knows about. Loaders write the same `type` strings; new types fall into
 * `Other` and pick up a humanized label from the raw key.
 */
const TYPE_CATALOG = [
  // ── Body ──────────────────────────────────────────────────────────────
  { id: 'weight',                          label: 'Weight',                  unit: 'kg',          category: 'body' },
  { id: 'body_fat',                        label: 'Body fat',                unit: '%',           category: 'body' },
  { id: 'skeletal_muscle',                 label: 'Skeletal muscle',         unit: '%',           category: 'body' },
  { id: 'subcutaneous_fat',                label: 'Subcutaneous fat',        unit: '%',           category: 'body' },
  { id: 'visceral_fat',                    label: 'Visceral fat',            unit: '',            category: 'body' },
  { id: 'body_water',                      label: 'Body water',              unit: '%',           category: 'body' },
  { id: 'bone_mass',                       label: 'Bone mass',               unit: 'kg',          category: 'body' },
  { id: 'protein',                         label: 'Protein',                 unit: '%',           category: 'body' },
  { id: 'waist',                           label: 'Waist',                   unit: 'cm',          category: 'body' },
  { id: 'fat_free_mass',                   label: 'Fat-free mass',           unit: 'kg',          category: 'body' },
  { id: 'muscle_mass',                     label: 'Muscle mass',             unit: 'kg',          category: 'body' },
  // ── Circumferences (cm) ───────────────────────────────────────────────
  { id: 'waist_navel',                     label: 'Waist (navel)',           unit: 'cm',          category: 'body' },
  { id: 'waist_narrowest',                 label: 'Waist (narrowest)',       unit: 'cm',          category: 'body' },
  { id: 'hip',                             label: 'Hip',                     unit: 'cm',          category: 'body' },
  { id: 'chest',                           label: 'Chest',                   unit: 'cm',          category: 'body' },
  { id: 'thigh',                           label: 'Thigh',                   unit: 'cm',          category: 'body' },
  { id: 'arm_relaxed',                     label: 'Arm (relaxed)',           unit: 'cm',          category: 'body' },
  { id: 'arm_flexed',                      label: 'Arm (flexed)',            unit: 'cm',          category: 'body' },
  { id: 'neck',                            label: 'Neck',                    unit: 'cm',          category: 'body' },
  { id: 'calf',                            label: 'Calf',                    unit: 'cm',          category: 'body' },
  { id: 'shoulders',                       label: 'Shoulders',               unit: 'cm',          category: 'body' },
  // ── Vitals ────────────────────────────────────────────────────────────
  { id: 'resting_hr',                      label: 'Resting HR',              unit: 'bpm',         category: 'vitals' },
  { id: 'heart_rate_sleeping',             label: 'HR (sleeping)',           unit: 'bpm',         category: 'vitals' },
  { id: 'hrv',                             label: 'HRV',                     unit: 'ms',          category: 'vitals' },
  { id: 'vo2max',                          label: 'VO₂ max',                 unit: 'ml/kg/min',   category: 'vitals' },
  { id: 'blood_oxygen',                    label: 'Blood oxygen',            unit: '%',           category: 'vitals' },
  { id: 'respiratory_rate',                label: 'Respiratory rate',        unit: 'bpm',         category: 'vitals' },
  { id: 'body_temp_deviation',             label: 'Body temp deviation',     unit: '°C',          category: 'vitals' },
  // ── Activity ──────────────────────────────────────────────────────────
  { id: 'steps',                           label: 'Steps',                   unit: '',            category: 'activity' },
  // ── Sleep (signals — the session itself lives on Sleep page) ──────────
  { id: 'sleep_regularity',                label: 'Sleep regularity',        unit: '%',           category: 'sleep' },
  { id: 'sleep_movements_per_hour',        label: 'Movements / h',           unit: '/h',          category: 'sleep' },
  { id: 'sleep_latency',                   label: 'Sleep latency',           unit: 'min',         category: 'sleep' },
  { id: 'snore_time',                      label: 'Snore time',              unit: 'min',         category: 'sleep' },
  { id: 'coughs_per_hour',                 label: 'Coughs / h',              unit: '/h',          category: 'sleep' },
  { id: 'breathing_disruptions_per_hour',  label: 'Breathing disruptions',   unit: '/h',          category: 'sleep' },
];

type CategoryId = 'all' | 'body' | 'vitals' | 'activity' | 'sleep' | 'other';

const CATEGORIES: Array<{ id: CategoryId; label: string }> = [
  { id: 'all',      label: 'All' },
  { id: 'body',     label: 'Body' },
  { id: 'vitals',   label: 'Vitals' },
  { id: 'activity', label: 'Activity' },
  { id: 'sleep',    label: 'Sleep' },
  { id: 'other',    label: 'Other' },
];

const CATALOG_BY_ID = new Map(TYPE_CATALOG.map((t) => [t.id, t]));

function categoryOf(type: string): CategoryId {
  return (CATALOG_BY_ID.get(type)?.category ?? 'other') as CategoryId;
}

function labelFor(type: string): string {
  const entry = CATALOG_BY_ID.get(type);
  if (entry) return entry.label;
  return type
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function unitFor(type: string): string | null {
  const entry = CATALOG_BY_ID.get(type);
  return entry?.unit ?? null;
}

// ─── Screen ──────────────────────────────────────────────────────────────

export function HealthMeasurementsScreen() {
  const [category, setCategory] = useState<CategoryId>('all');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);

  const allQ = useMeasurements();
  const all = useMemo(() => allQ.data?.measurements ?? [], [allQ.data]);
  const grouped = useMemo(() => groupByType(all), [all]);

  // Selecting a new category clears any type focus so we don't end up in
  // an inconsistent "showing Body category, but type chip Sleep is active".
  const setCategoryAndClear = (c: CategoryId) => {
    setCategory(c);
    setTypeFilter('');
  };

  // Filter the grouped metrics down to the selected category. "All" shows
  // every metric the user has data for.
  const visibleGroups = useMemo(() => {
    if (category === 'all') return grouped;
    return grouped.filter((g) => categoryOf(g.type) === category);
  }, [grouped, category]);

  // Type chips: catalog entries in the current category + any user-logged
  // types we don't know about (in 'all' or 'other'). Greys out chips for
  // metrics the user hasn't started.
  const typeChipIds = useMemo(() => {
    const knownInCategory = TYPE_CATALOG
      .filter((t) => category === 'all' || t.category === category)
      .map((t) => t.id);
    const userHas = grouped
      .map((g) => g.type)
      .filter((t) => category === 'all' || categoryOf(t) === category);
    // Union, preserving "user has it" order in front.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of userHas) {
      if (!seen.has(t)) { out.push(t); seen.add(t); }
    }
    for (const t of knownInCategory) {
      if (!seen.has(t)) { out.push(t); seen.add(t); }
    }
    return out;
  }, [grouped, category]);

  const selectedGroup = typeFilter
    ? grouped.find((g) => g.type === typeFilter) ?? null
    : null;

  const filteredList = useMemo(
    () => (typeFilter ? all.filter((m) => m.type === typeFilter) : all),
    [all, typeFilter],
  );

  // Modal preset — when a type is focused, "+ Log measurement" pre-fills it.
  const modalPreset =
    typeFilter && selectedGroup
      ? { type: typeFilter, unit: selectedGroup.unit }
      : null;

  return (
    <div className={styles.page}>
      <header
        className={styles.head}
        style={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div>
          <h2 className={styles.title}>Measurements</h2>
          <div className={styles.subtitle}>
            Single-metric time series — weight, HRV, resting HR, VO₂, steps,
            anything you want to trend. Agents read these to spot drift.
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className="kos-btn kos-btn-primary"
            onClick={() => setModalOpen(true)}
          >
            + Log measurement
          </button>
        </div>
      </header>

      {/* Category bar — top-level grouping. */}
      <div
        style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}
      >
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`kos-filter-chip ${category === c.id ? 'kos-filter-chip-active' : ''}`}
            onClick={() => setCategoryAndClear(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Type chips inside the selected category. Hidden in 'all' when
          we'd otherwise render too many; in that case we show the metric
          grid below and let the user click cards. */}
      {category !== 'all' && (
        <div
          style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}
        >
          <button
            type="button"
            className={`kos-filter-chip ${typeFilter === '' ? 'kos-filter-chip-active' : ''}`}
            onClick={() => setTypeFilter('')}
          >
            All in {labelForCategory(category)}
          </button>
          {typeChipIds.map((t) => {
            const has = grouped.some((g) => g.type === t);
            return (
              <button
                key={t}
                type="button"
                className={`kos-filter-chip ${typeFilter === t ? 'kos-filter-chip-active' : ''}`}
                onClick={() => setTypeFilter(t)}
                style={has ? undefined : { opacity: 0.55 }}
                title={has ? undefined : 'No data yet — log a first reading'}
              >
                {labelFor(t)}
              </button>
            );
          })}
        </div>
      )}

      {/* Visualization. Empty when there are no points in the category;
          BigChart for a focused type; otherwise a sparkline grid. */}
      {allQ.isLoading ? (
        <div className={styles.muted}>Loading…</div>
      ) : visibleGroups.length === 0 ? (
        <div className={styles.empty}>
          {category === 'all'
            ? 'No measurements yet.'
            : `No ${labelForCategory(category)} measurements yet.`}
        </div>
      ) : selectedGroup ? (
        <TimeSeriesChart
          label={selectedGroup.label}
          unit={selectedGroup.unit}
          points={selectedGroup.points}
          subtitle={selectedGroup.unit ?? undefined}
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
            marginBottom: 18,
          }}
        >
          {visibleGroups.map((g) => (
            <MetricCard
              key={g.type}
              group={g}
              onClick={() => {
                setCategory(categoryOf(g.type));
                setTypeFilter(g.type);
              }}
            />
          ))}
        </div>
      )}

      {/* Recent log */}
      {grouped.length > 0 && (
        <>
          <h3 style={{ margin: '24px 0 8px', fontSize: 14, fontWeight: 600 }}>
            {typeFilter ? `${labelFor(typeFilter)} log` : 'Recent log'}{' '}
            <span
              style={{
                fontFeatureSettings: "'tnum'",
                color: 'var(--ink-4)',
                fontWeight: 400,
              }}
            >
              {filteredList.length}
            </span>
          </h3>
          <div className={styles.list}>
            {filteredList.slice(0, 50).map((m) => (
              <div key={m.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{labelFor(m.type)}</div>
                  <div className={styles.rowMeta}>
                    {new Date(m.recordedAt).toLocaleString()} · {m.source}
                    {m.notes ? ` · ${m.notes}` : ''}
                  </div>
                </div>
                <span className={styles.rowValue}>
                  {m.value} {m.unit ?? unitFor(m.type) ?? ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <MeasurementFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        preset={modalPreset}
      />
    </div>
  );
}

function labelForCategory(c: CategoryId): string {
  return CATEGORIES.find((x) => x.id === c)?.label ?? c;
}

// ─── Grouping + sparkline card ───────────────────────────────────────────

interface MetricGroup {
  type: string;
  label: string;
  unit: string | null;
  points: ChartPoint[];
  latest: number;
  earliest: number;
  min: number;
  max: number;
}

function groupByType(measurements: MeasurementDto[]): MetricGroup[] {
  const map = new Map<string, MetricGroup>();
  for (const m of measurements) {
    if (typeof m.value !== 'number') continue;
    const t = new Date(m.recordedAt).getTime();
    const existing = map.get(m.type);
    if (existing) {
      existing.points.push({ t, v: m.value });
    } else {
      map.set(m.type, {
        type: m.type,
        label: labelFor(m.type),
        unit: m.unit ?? unitFor(m.type),
        points: [{ t, v: m.value }],
        latest: m.value,
        earliest: m.value,
        min: m.value,
        max: m.value,
      });
    }
  }
  const groups = [...map.values()].map((g) => {
    g.points.sort((a, b) => a.t - b.t);
    g.earliest = g.points[0]!.v;
    g.latest = g.points[g.points.length - 1]!.v;
    g.min = g.points.reduce((acc, p) => Math.min(acc, p.v), Infinity);
    g.max = g.points.reduce((acc, p) => Math.max(acc, p.v), -Infinity);
    return g;
  });
  groups.sort((a, b) => {
    const aT = a.points[a.points.length - 1]!.t;
    const bT = b.points[b.points.length - 1]!.t;
    return bT - aT;
  });
  return groups;
}

function MetricCard({ group, onClick }: { group: MetricGroup; onClick: () => void }) {
  const delta = group.latest - group.earliest;
  const deltaPct =
    group.earliest !== 0 ? (delta / Math.abs(group.earliest)) * 100 : 0;
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`Open ${group.label} chart`}
      style={{
        textAlign: 'left',
        background: hover
          ? 'color-mix(in oklab, var(--accent) 5%, var(--canvas))'
          : 'var(--canvas)',
        border: `1px solid ${hover ? 'var(--accent)' : 'var(--line)'}`,
        borderRadius: 8,
        padding: '12px 14px',
        cursor: 'pointer',
        font: 'inherit',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
        transition: 'background-color 120ms, border-color 120ms',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{group.label}</span>
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: hover ? 'var(--accent)' : 'var(--ink-4)',
            transition: 'color 120ms',
          }}
        >
          Open ›
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 20,
            fontWeight: 700,
            fontFeatureSettings: "'tnum'",
          }}
        >
          {formatValue(group.latest)}
        </span>
        {group.unit && (
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{group.unit}</span>
        )}
        {group.points.length > 1 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 12,
              fontFeatureSettings: "'tnum'",
              color:
                delta > 0 ? 'var(--warn)' : delta < 0 ? 'var(--accent)' : 'var(--ink-4)',
            }}
            title={`From ${formatValue(group.earliest)} → ${formatValue(group.latest)}`}
          >
            {delta > 0 ? '+' : ''}
            {formatValue(delta)} ({deltaPct > 0 ? '+' : ''}
            {deltaPct.toFixed(1)}%)
          </span>
        )}
      </div>
      <div style={{ pointerEvents: 'none' }}>
        <Sparkline points={group.points} width={200} height={36} />
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
        {group.points.length} points · {formatRelative(group.points[0]!.t)} →{' '}
        {formatRelative(group.points[group.points.length - 1]!.t)}
      </div>
    </button>
  );
}

function Sparkline({
  points,
  width,
  height,
}: {
  points: ChartPoint[];
  width: number;
  height: number;
}) {
  if (points.length === 0) return null;
  if (points.length === 1) {
    return (
      <svg width={width} height={height}>
        <circle cx={width / 2} cy={height / 2} r={3} fill="var(--accent)" />
      </svg>
    );
  }
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.v);
  const minT = Math.min(...xs);
  const maxT = Math.max(...xs);
  const minV = Math.min(...ys);
  const maxV = Math.max(...ys);
  const padY = (maxV - minV) * 0.15 || 1;
  const yScale = (v: number): number => {
    const lo = minV - padY;
    const hi = maxV + padY;
    if (hi === lo) return height / 2;
    return height - ((v - lo) / (hi - lo)) * height;
  };
  const xScale = (t: number): number => {
    if (maxT === minT) return width / 2;
    return ((t - minT) / (maxT - minT)) * width;
  };
  const d = points
    .map((p) => `${xScale(p.t).toFixed(2)},${yScale(p.v).toFixed(2)}`)
    .join(' ');
  const lastX = xScale(xs[xs.length - 1]!);
  const lastY = yScale(ys[ys.length - 1]!);
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={d}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill="var(--accent)" />
    </svg>
  );
}

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  const abs = Math.abs(v);
  return abs >= 100 ? v.toFixed(1) : v.toFixed(2);
}

function formatRelative(t: number): string {
  const days = Math.round((Date.now() - t) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}
